import styled from 'styled-components';
import { TabPanel as TabPanelBase } from '@mui/lab';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import dayjs from 'dayjs';
import { DateRangeValue } from '@elementary/common-ui/components/DateRangePicker';
import { DAYS_BACK } from '@elementary/common-ui/constants/urlParams';
import { Dependencies } from 'shared/components/DependenciesTable';
import ExecutionHistory from 'shared/components/ExecutionHistory';
import { useDefaultDateRange } from 'shared/components/ExecutionHistory';
import PipelineExecutionInfo from 'shared/components/Pipeline/PipelineExecutionInfo';
import ModelFailureDetails from 'shared/components/Asset/executionInfo/ModelFailureDetails';
import TestOverviewExecutionDetails from 'shared/components/TestOverviewExecutionDetails';
import useIncidentMetadataOptions from 'incidents/hooks/useIncidentMetadataOptions';
import { FlexColumn } from '@elementary/common-ui/components';
import { IncidentOverviewData } from '../../../types';
import IncidentTimeline from './IncidentTimeline';

const TabsContainer = styled(FlexColumn)`
  height: 100%;
  min-height: 0;
  flex: 1;
  padding-top: 8px;
`;

const TabPanel = styled(TabPanelBase)`
  &[hidden] {
    display: none;
  }
  &:not([hidden]) {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const DependenciesWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  > div {
    flex: 1;
    min-height: 0;
    height: auto;
    justify-content: flex-start;

    > div:last-child {
      flex: 1;
      min-height: 0;
      max-height: none !important;
    }
  }
`;

type Props = {
  incident: IncidentOverviewData;
  currentTab: 'timeline' | 'execution' | 'dependencies';
};

const IncidentOverviewTabs = ({ incident }: Props) => {
  const [searchParams] = useSearchParams();
  const defaultDaysBack = useDefaultDateRange();
  const { usersData } = useIncidentMetadataOptions();

  const daysBackParam = searchParams.get(DAYS_BACK);
  const parsedDaysBack = daysBackParam ? parseInt(daysBackParam, 10) : null;
  const daysBack =
    parsedDaysBack !== null &&
    Number.isInteger(parsedDaysBack) &&
    parsedDaysBack >= 0
      ? parsedDaysBack
      : defaultDaysBack;

  const dateRange: DateRangeValue = {
    start: dayjs().subtract(daysBack, 'days').startOf('day'),
    end: dayjs().endOf('hour'),
  };

  return (
    <TabsContainer>
      <TabPanel value="execution">
        <ScrollableContent>
          {incident.sourceType === 'pipeline'
            ? incident.pipelineData && (
                <PipelineExecutionInfo data={incident.pipelineData} />
              )
            : incident.sourceId &&
              incident.sourceType && (
                <ExecutionHistory
                  DetailsComponent={
                    incident.sourceType === 'model'
                      ? ModelFailureDetails
                      : TestOverviewExecutionDetails
                  }
                  id={incident.sourceId}
                  sourceType={incident.sourceType}
                  dateRange={dateRange}
                />
              )}
        </ScrollableContent>
      </TabPanel>

      <TabPanel value="dependencies">
        <DependenciesWrapper>
          {incident.tableAssetId && (
            <Dependencies
              tableAssetId={incident.tableAssetId}
              testName={incident.testName || incident.displayName}
              resource={{
                type: incident.isColumn ? 'COLUMN' : 'TABLE',
                name:
                  incident.fullyQualifiedName ||
                  incident.testName ||
                  incident.displayName,
              }}
            />
          )}
        </DependenciesWrapper>
      </TabPanel>

      <TabPanel value="timeline">
        <ScrollableContent>
          <IncidentTimeline
            events={incident.events}
            testName={incident.testName}
            modelName={incident.fullyQualifiedName}
            usersData={usersData}
          />
        </ScrollableContent>
      </TabPanel>
    </TabsContainer>
  );
};

export default IncidentOverviewTabs;
